// frontend/src/pages/freelancer/FreelancerServiceEdit.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import "./freelancerStyles.css";
import PriceTiersEditor from "../../components/PriceTiersEditor";

// Utility function to convert text to user-friendly Title Case
const toUserFriendly = (text) => {
  if (!text) return "";
  return text
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

function FreelancerServiceEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const isBoothRenterRoute = location.pathname.startsWith(
    "/dashboard/booth-renter",
  );
  const basePath = isBoothRenterRoute
    ? "/dashboard/booth-renter"
    : "/dashboard/freelancer";

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    durationMinutes: "",
    category: "haircut",
    applicableTo: [],
    pricingType: "fixed",
    maxPrice: "",
    hourlyRate: "",
    maxHoursPerBooking: 8,
    priceTiers: [],
    requiresConsultation: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [staffProfessionalTypes, setStaffProfessionalTypes] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  // Fetch service details
  useEffect(() => {
    const fetchService = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        const res = await axios.get(`${baseURL}/api/staff/services`, {
          headers,
        });
        const service = res.data.services?.find((s) => s.id === serviceId);

        if (!service) {
          setErrorMsg("Service not found");
          setTimeout(() => navigate(`${basePath}/services`), 2000);
          return;
        }

        setFormData({
          name: service.name || "",
          description: service.description || "",
          price: service.price || "",
          durationMinutes: service.durationMinutes || "",
          category: service.category || "haircut",
          applicableTo: service.applicableTo || [],
          pricingType: service.pricingType || "fixed",
          maxPrice: service.maxPrice || "",
          hourlyRate: service.hourlyRate || "",
          maxHoursPerBooking: service.maxHoursPerBooking || 8,
          priceTiers: Array.isArray(service.priceTiers)
            ? service.priceTiers.map((tier) => ({
                label: tier.label || "",
                price: tier.price ?? "",
                durationMinutes: tier.durationMinutes ?? "",
              }))
            : [],
          requiresConsultation: service.requiresConsultation || false,
        });

        // Fetch staff profile for professional types
        const staffResponse = await axios.get(
          `${baseURL}/api/freelancer/profile`,
          {
            headers,
          },
        );
        const staff = staffResponse.data.staff;
        const types = staff?.professionalTypes || [];
        setStaffProfessionalTypes(types);

        // Fetch business info to get business types
        const businessResponse = await axios.get(
          `${baseURL}/api/business/${staff.businessId}`,
          { headers },
        );
        const businessTypes = businessResponse.data?.type || [];

        // Fetch service categories based on business types (e.g., ['hair-studio', 'salon'])
        if (businessTypes && businessTypes.length > 0) {
          try {
            // Join business types with comma for API query
            const businessCategoriesParam = businessTypes.join(",");
            const categoriesResponse = await axios.get(
              `${baseURL}/api/platform-settings/service-categories?businessCategories=${businessCategoriesParam}`,
            );
            const fetchedCategories = categoriesResponse.data.categories || [];
            if (fetchedCategories.length > 0) {
              setServiceCategories(fetchedCategories);
            } else {
              // No categories returned, use fallback
              setServiceCategories([
                { value: "haircut", label: "Haircut" },
                { value: "color", label: "Color" },
                { value: "styling", label: "Styling" },
                { value: "treatment", label: "Treatment" },
                { value: "other", label: "Other" },
              ]);
            }
          } catch (catErr) {
            // Fallback to default categories
            setServiceCategories([
              { value: "haircut", label: "Haircut" },
              { value: "color", label: "Color" },
              { value: "styling", label: "Styling" },
              { value: "treatment", label: "Treatment" },
              { value: "other", label: "Other" },
            ]);
          }
        } else {
          // Independent pros (freelancers / booth renters) have no business to
          // filter by - fetch ALL service categories so they can pick any.
          try {
            const allResponse = await axios.get(
              `${baseURL}/api/platform-settings/service-categories?businessCategories=all`,
              { headers },
            );
            const allCats = allResponse.data.categories || [];
            if (allCats.length > 0) {
              setServiceCategories(allCats);
            } else {
              setServiceCategories([
                { value: "haircut", label: "Haircut" },
                { value: "color", label: "Color" },
                { value: "styling", label: "Styling" },
                { value: "treatment", label: "Treatment" },
                { value: "other", label: "Other" },
              ]);
            }
          } catch (allErr) {
            setServiceCategories([
              { value: "haircut", label: "Haircut" },
              { value: "color", label: "Color" },
              { value: "styling", label: "Styling" },
              { value: "treatment", label: "Treatment" },
              { value: "other", label: "Other" },
            ]);
          }
        }
        setLoadingCategories(false);

        if (service.serviceImage) {
          // Check if image URL is already absolute (Azure Blob) or relative (local)
          const isAbsoluteUrl =
            service.serviceImage.startsWith("http://") ||
            service.serviceImage.startsWith("https://");
          setExistingImage(
            isAbsoluteUrl
              ? service.serviceImage
              : `${baseURL}${service.serviceImage}`,
          );
        }
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login");
        } else {
          setErrorMsg("Unable to load service. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [serviceId, getAuthHeaders, getApiBaseUrl, navigate, basePath]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleApplicableToToggle = (type) => {
    setFormData((prev) => {
      const isSelected = prev.applicableTo.includes(type);
      const newApplicableTo = isSelected
        ? prev.applicableTo.filter((t) => t !== type)
        : [...prev.applicableTo, type].slice(0, 3);
      return { ...prev, applicableTo: newApplicableTo };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setErrorMsg("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg("Image file size must be less than 5MB");
      return;
    }

    setImageFile(file);
    setErrorMsg("");

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Image upload function - COMMENTED OUT PER USER REQUEST
  /*
  const uploadImage = async () => {
    if (!imageFile) return null;

    try {
      setUploadingImage(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return null;

      const formData = new FormData();
      formData.append("image", imageFile); // Backend expects 'image' field name

      const uploadRes = await axios.post(
        `${baseURL}/api/staff/services/upload-image`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return uploadRes.data.imageUrl;
    } catch (err) {
      throw new Error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };
  */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    // Validation
    if (!formData.name.trim()) {
      setErrorMsg("Service name is required");
      return;
    }
    if (
      formData.pricingType !== "range" &&
      formData.pricingType !== "hourly" &&
      (!formData.price || formData.price <= 0)
    ) {
      setErrorMsg("Valid price is required");
      return;
    }
    if (
      formData.pricingType !== "hourly" &&
      formData.pricingType !== "range" &&
      (!formData.durationMinutes || formData.durationMinutes < 5)
    ) {
      setErrorMsg("Duration must be at least 5 minutes");
      return;
    }

    try {
      setSubmitting(true);

      // Upload new image if selected - COMMENTED OUT PER USER REQUEST
      /*
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          setErrorMsg("Failed to upload image");
          return;
        }
      }
      */

      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Prepare update data
      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        durationMinutes: parseInt(formData.durationMinutes),
        category: formData.category,
        applicableTo: formData.applicableTo,
        pricingType: formData.pricingType,
      };

      // Add conditional pricing fields
      // F2: hourly = rate x hours; range = customer picks one of the options
      if (formData.pricingType === "hourly") {
        const rate = parseFloat(formData.hourlyRate);
        if (Number.isFinite(rate)) {
          updateData.hourlyRate = rate;
          updateData.price = rate;
        }
        updateData.maxHoursPerBooking = parseInt(
          formData.maxHoursPerBooking || 8,
          10,
        );
        // Duration field is hidden for hourly - the engine bills 60 min per hour.
        updateData.durationMinutes = 60;
      }
      if (formData.pricingType === "range") {
        const usableTiers = (formData.priceTiers || [])
          .map((tier) => ({
            label: String(tier.label || "").trim(),
            price: parseFloat(tier.price),
            durationMinutes: parseInt(tier.durationMinutes, 10),
          }))
          .filter((tier) => tier.label && Number.isFinite(tier.price));

        if (usableTiers.length < 2) {
          setErrorMsg(
            "Add at least 2 priced options (for example: Shoulder length $150, Mid-back $220).",
          );
          return;
        }

        const missingMinutes = usableTiers.some(
          (tier) =>
            !Number.isFinite(tier.durationMinutes) || tier.durationMinutes < 5,
        );
        if (missingMinutes) {
          setErrorMsg("Give every option a duration of at least 5 minutes.");
          return;
        }

        const tierPrices = usableTiers.map((t) => t.price);
        updateData.priceTiers = usableTiers;
        updateData.price = Math.min(...tierPrices);
        updateData.maxPrice = Math.max(...tierPrices);
        // Duration field is hidden for range - use the longest option length.
        updateData.durationMinutes = Math.max(
          ...usableTiers.map((t) => Number(t.durationMinutes) || 0),
        );
      }
      if (formData.pricingType === "variable") {
        updateData.requiresConsultation = formData.requiresConsultation;
      }
      // F1: per-unit service - base price is the per-unit price; max 10 units
      if (formData.pricingType === "per_unit") {
        updateData.pricePerUnit = parseFloat(formData.price);
        updateData.maxUnitsPerBooking = 10;
      }

      // Only include image if a new one was uploaded - COMMENTED OUT
      /*
      if (imageUrl) {
        updateData.serviceImage = imageUrl;
      }
      */

      await axios.patch(
        `${baseURL}/api/staff/services/${serviceId}`,
        updateData,
        { headers },
      );

      setSuccessMsg("Service updated successfully!");
      setTimeout(() => {
        navigate(`${basePath}/services`);
      }, 1500);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(
          err?.response?.data?.message ||
            "Failed to update service. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading service...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div style={{ maxWidth: "700px", margin: "0 auto", padding: "20px" }}>
        <button
          onClick={() => navigate(`${basePath}/services`)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            marginBottom: "20px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            background: "#fff",
            color: "#333",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          <span>←</span> Back to Services
        </button>

        <div style={{ marginBottom: "30px" }}>
          <h2 style={{ margin: "0 0 8px 0" }}>Edit Service</h2>
          <p style={{ margin: 0, color: "#666" }}>
            Update your service details
          </p>
        </div>

        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {successMsg && (
          <div
            style={{
              background: "#e6ffed",
              border: "1px solid #03543f",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#03543f" }}>{successMsg}</p>
          </div>
        )}

        <div className="dashboard-card" style={{ padding: "30px" }}>
          <form onSubmit={handleSubmit}>
            {/* Service Name */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Service Name <span style={{ color: "#c00" }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                maxLength={100}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
                placeholder="e.g., Haircut & Style"
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                placeholder="Describe what this service includes..."
              />
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Category */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  background: "#fff",
                  cursor: "pointer",
                  boxSizing: "border-box",
                }}
              >
                {serviceCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Price and Duration */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              {formData.pricingType !== "range" &&
                formData.pricingType !== "hourly" && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Price (USD) <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="0.00"
                />
              </div>
              )}

              {formData.pricingType !== "hourly" &&
                formData.pricingType !== "range" && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Duration (minutes) <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="durationMinutes"
                  value={formData.durationMinutes}
                  onChange={handleChange}
                  required
                  min="5"
                  step="5"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="30"
                />
              </div>
              )}
            </div>

            {/* Pricing Type */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Pricing Type
              </label>
              <select
                name="pricingType"
                value={formData.pricingType}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  background: "#fff",
                  cursor: "pointer",
                  boxSizing: "border-box",
                }}
              >
                <option value="fixed">Fixed Price</option>
                <option value="per_unit">Per Unit (e.g. per nail)</option>
                <option value="hourly">Hourly (rate x hours)</option>
                <option value="range">Options (customer picks one)</option>
                {/* From / Variable hidden until properly wired */}
              </select>
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                {formData.pricingType === "fixed" &&
                  "Exact price for the service"}
                {formData.pricingType === "from" &&
                  "Starting price, final may vary based on requirements"}
                {formData.pricingType === "range" &&
                  "Price varies within a range based on service complexity"}
                {formData.pricingType === "hourly" &&
                  "Charged per hour of work"}
                {formData.pricingType === "variable" &&
                  "Price determined after consultation"}
                {formData.pricingType === "per_unit" &&
                  "Price is per unit (e.g. per nail). Enter the per-unit price above and the time per unit as the duration. Customers choose the quantity."}
              </p>
            </div>

            {/* Price options - Only for range pricing (customer picks one) */}
            {formData.pricingType === "range" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Price options
                </label>
                <PriceTiersEditor
                  tiers={formData.priceTiers || []}
                  onChange={(nextTiers) =>
                    setFormData((prev) => ({ ...prev, priceTiers: nextTiers }))
                  }
                />
              </div>
            )}

            {/* Hourly Rate - Only for hourly pricing */}
            {formData.pricingType === "hourly" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Hourly Rate (USD) <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="Rate per hour"
                />
                <p
                  style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
                >
                  The appointment length is the number of hours the customer
                  books (hours x 60 minutes).
                </p>
              </div>
            )}

            {/* Max hours - Only for hourly pricing */}
            {formData.pricingType === "hourly" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Max hours per booking
                </label>
                <input
                  type="number"
                  name="maxHoursPerBooking"
                  value={formData.maxHoursPerBooking}
                  onChange={handleChange}
                  min="1"
                  max="24"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                />
                <p
                  style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
                >
                  Customers pick how many hours they need. Total price = hourly
                  rate x hours, and the appointment length scales the same way.
                </p>
              </div>
            )}

            {/* Requires Consultation - Only for variable pricing */}
            {formData.pricingType === "variable" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#001d4a",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="requiresConsultation"
                    checked={formData.requiresConsultation}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        requiresConsultation: e.target.checked,
                      }))
                    }
                    style={{ width: "18px", height: "18px" }}
                  />
                  Requires consultation before booking
                </label>
                <p
                  style={{
                    margin: "5px 0 0 28px",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Customer will need to contact you for a quote before booking
                </p>
              </div>
            )}

            {/* Applicable To (Professional Types) */}
            {staffProfessionalTypes.length > 1 && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Service Applies To
                </label>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "10px",
                  }}
                >
                  Leave empty if this service applies to all your professional
                  types. Otherwise, select which types (max 3).
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {staffProfessionalTypes.map((type) => (
                    <label
                      key={type}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 12px",
                        border: "2px solid",
                        borderColor: formData.applicableTo.includes(type)
                          ? "#001d4a"
                          : "#ddd",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background: formData.applicableTo.includes(type)
                          ? "#e8f4f8"
                          : "white",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.applicableTo.includes(type)}
                        onChange={() => handleApplicableToToggle(type)}
                        disabled={
                          formData.applicableTo.length >= 3 &&
                          !formData.applicableTo.includes(type)
                        }
                        style={{ marginRight: "8px" }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>
                        {toUserFriendly(type)}
                      </span>
                    </label>
                  ))}
                </div>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Selected {formData.applicableTo.length} type
                  {formData.applicableTo.length !== 1 ? "s" : ""}
                </p>
              </div>
            )}

            {/* Service Image Upload - COMMENTED OUT PER USER REQUEST
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Service Image
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleImageChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  cursor: "pointer",
                }}
              />
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                Upload a new image to replace the existing one (max 5MB,
                JPEG/PNG/GIF/WebP)
              </p>
            </div>

            {/* Image Preview */}
            {/* {(imagePreview || existingImage) && (
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "12px",
                    color: "#001d4a",
                  }}
                >
                  {imagePreview ? "New Image Preview" : "Current Image"}
                </label>
                <img
                  src={imagePreview || existingImage}
                  alt="Service preview"
                  style={{
                    maxWidth: "300px",
                    maxHeight: "200px",
                    borderRadius: "8px",
                    border: "2px solid #e0e0e0",
                  }}
                />
              </div>
            )} */}
            {/* END Service Image Upload */}

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: "15px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={() => navigate(`${basePath}/services`)}
                disabled={submitting}
                style={{
                  padding: "12px 30px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#333",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || uploadingImage}
                className="btn-primary"
                style={{
                  padding: "12px 30px",
                  minWidth: "140px",
                }}
              >
                {uploadingImage
                  ? "Uploading Image..."
                  : submitting
                    ? "Updating..."
                    : "Update Service"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerServiceEdit;
